import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type PremiumSnapshot = { isPremium: boolean; loading: boolean };
type Listener = (snapshot: PremiumSnapshot) => void;

const snapshots = new Map<string, PremiumSnapshot>();
const listeners = new Map<string, Set<Listener>>();
const inflight = new Map<string, Promise<void>>();
const channels = new Map<string, ReturnType<typeof supabase.channel>>();

const publish = (userId: string, snapshot: PremiumSnapshot) => {
  snapshots.set(userId, snapshot);
  listeners.get(userId)?.forEach((listener) => listener(snapshot));
};

const loadPremiumStatus = (userId: string, force = false) => {
  if (!force && inflight.has(userId)) return inflight.get(userId) as Promise<void>;

  const request = (async () => {
    if (!snapshots.has(userId)) publish(userId, { isPremium: false, loading: true });
    try {
      const [rolesResult, subscriptionResult] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId).in("role", ["premium", "owner", "admin"]),
        supabase
          .from("subscriptions")
          .select("tier, status, trial_ends_at, subscription_ends_at")
          .eq("user_id", userId)
          .maybeSingle(),
      ]);

      if (rolesResult.data?.length) {
        publish(userId, { isPremium: true, loading: false });
        return;
      }

      const subscription = subscriptionResult.data;
      const now = Date.now();
      const expiry = subscription?.subscription_ends_at
        ? new Date(subscription.subscription_ends_at).getTime()
        : null;
      const trialExpiry = subscription?.trial_ends_at
        ? new Date(subscription.trial_ends_at).getTime()
        : null;
      const active = subscription?.tier === "premium" && (
        (subscription.status === "trial" && trialExpiry !== null && trialExpiry > now) ||
        (subscription.status === "active" && (expiry === null || expiry > now)) ||
        (subscription.status === "cancelled" && expiry !== null && expiry > now)
      );
      publish(userId, { isPremium: Boolean(active), loading: false });
    } catch (error) {
      console.error("Error checking premium status:", error);
      publish(userId, { isPremium: false, loading: false });
    } finally {
      inflight.delete(userId);
    }
  })();

  inflight.set(userId, request);
  return request;
};

const subscribe = (userId: string, listener: Listener) => {
  const userListeners = listeners.get(userId) ?? new Set<Listener>();
  userListeners.add(listener);
  listeners.set(userId, userListeners);

  if (!channels.has(userId)) {
    const channel = supabase
      .channel(`subscription-changes:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${userId}` },
        () => { void loadPremiumStatus(userId, true); }
      )
      .subscribe();
    channels.set(userId, channel);
  }

  return () => {
    const current = listeners.get(userId);
    current?.delete(listener);
    if (current?.size) return;
    listeners.delete(userId);
    const channel = channels.get(userId);
    if (channel) void supabase.removeChannel(channel);
    channels.delete(userId);
  };
};

export const usePremiumStatus = (userId?: string) => {
  const [snapshot, setSnapshot] = useState<PremiumSnapshot>(() =>
    userId ? snapshots.get(userId) ?? { isPremium: false, loading: true } : { isPremium: false, loading: false }
  );

  useEffect(() => {
    if (!userId) {
      setSnapshot({ isPremium: false, loading: false });
      return;
    }
    setSnapshot(snapshots.get(userId) ?? { isPremium: false, loading: true });
    const unsubscribe = subscribe(userId, setSnapshot);
    void loadPremiumStatus(userId);
    return unsubscribe;
  }, [userId]);

  const refetch = useCallback(async () => {
    if (!userId) return;
    await loadPremiumStatus(userId, true);
  }, [userId]);

  return { ...snapshot, refetch };
};